/**
 * @typedef {import('./DashboardProps').default} Props
 * @typedef {import('./DashboardState').default} State
 * @typedef {import('../../shared/PropertyService/PropertyService').default} PropertyService
 */

import { AuthContext } from '../../../shared/AuthProvider';
import { translate } from '../../Internationalization';
import { withBreakpoint } from '../../../shared/BreakpointProvider';
import { withPropertyService } from '../../../service-container';
import AdsContainer from '../../../shared/BannerAds/AdsContainer/AdsContainer';
import ComparableMarketCard from '../shared/ComparableMarketCard';
import DashboardProgress from './DashboardProgress';
import FeaturedBlogs from '../../../Blog/FeaturedBlogs';
import FinanceHeader from './FinanceHeader';
import flags from '../../../shared/Flags';
import MortgageInformationCard from '../Finances/MortgageInformationCard';
import ProfessionalsCard from './ProfessionalsCard';
import PropertyCard from './PropertyCard/PropertyCard';
import PropertyValuations from '../Finances/PropertyValuations';
import PropertySubmenu from '../Properties/PropertySubmenu';
import React from 'react';
import TierGate from '../../../shared/TierGate';
import WelcomeCard from '../../shared/WelcomeCard/WelcomeCard';

/** @extends {React.Component<Props, State>} */
class Dashboard extends React.Component {
	componentDidMount() {
		this.syncDashboardPropertySelection();
	}

	componentDidUpdate(prevProps) {
		if (
			prevProps.property?.propertyList !==
			this.props.property?.propertyList
		) {
			this.syncDashboardPropertySelection();
		}
	}

	syncDashboardPropertySelection() {
		const propertyList = this.props.property?.propertyList ?? [];

		if (!propertyList.length) {
			return;
		}

		const primaryProperty =
			propertyList.find((property) => property.isPrimary) ??
			propertyList[0];

		if (
			!primaryProperty?.id ||
			this.props.property?.selectedProperty?.id === primaryProperty.id
		) {
			return;
		}

		this.props.property.setSelectedProperty(primaryProperty.id);
	}

	/**
	 * @returns {JSX.Element}
	 */
	/* eslint-disable max-lines-per-function -- long but not complex */
	render() {
		const dashboardProperty =
			this.props.property?.propertyList?.find(
				(property) => property.isPrimary
			) ?? this.props.property?.propertyList?.[0];
		const components = {
			ads: <AdsContainer userRole="homeowner" />,
			blog: (
				<TierGate
					fallback={null}
					flag={flags.homeowner.blogs}
					userTier="homeowner_tier"
				>
					<FeaturedBlogs
						key="homeowner-dashboard-blog"
						// @ts-ignore -- type added into props
						title={translate('agent.pages.dashboard.blog.title')}
						userRole="homeowner"
					/>
				</TierGate>
			),
			comps: (
				<TierGate
					fallback={null}
					flag={flags.homeowner.comparableMarketActivity}
					userTier="homeowner_tier"
				>
					<ComparableMarketCard key="homeowner-dashboard-comps" />
				</TierGate>
			),
			finances: (
				<TierGate
					fallback={null}
					flag={flags.homeowner.financesPage}
					userTier="homeowner_tier"
				>
					<FinanceHeader key="homeowner-dashboard-finances" />
				</TierGate>
			),
			mortgage: dashboardProperty?.id ? (
				<TierGate
					fallback={null}
					flag={flags.homeowner.mortgageInformation}
					userTier="homeowner_tier"
				>
					<MortgageInformationCard
						key="homeowner-dashboard-mortgage"
						match={{ params: { propertyId: dashboardProperty.id } }}
					/>
				</TierGate>
			) : null,
			progress: <DashboardProgress key="homeowner-dashboard-progress" />,
			property: <PropertyCard key="homeowner-dashboard-property" />,
			propertySubmenu: dashboardProperty?.id ? (
				<section
					className="border-y border-grey-300 bg-white py-2"
					key="homeowner-dashboard-property-submenu"
				>
					<PropertySubmenu propertyId={dashboardProperty.id} />
				</section>
			) : null,
			pros: (
				<ProfessionalsCard
					buttonClass="mx-auto"
					inputClass="mb-4"
					key="homeowner-dashboard-pros"
				/>
			),
			valuations: (
				<TierGate
					fallback={null}
					flag={flags.homeowner.avm}
					userTier="homeowner_tier"
				>
					<PropertyValuations key="homeowner-dashboard-valuations" />
				</TierGate>
			),
			welcome: <WelcomeCard key="homeowner-dashboard-welcome" />,
		};

		if (this.props.isBelowBreakpoint) {
			return (
				<React.Fragment>
					{components.welcome}
					<section className="app-block-list">
						{components.propertySubmenu}
						{components.mortgage}
						{components.property}
						{components.progress}
						{components.finances}
						{components.valuations}
						{components.ads}
						{components.comps}
						{components.pros}
						{components.blog}
					</section>
				</React.Fragment>
			);
		}

		return (
			<React.Fragment>
				{components.welcome}
				<section className="app-responsive-area app-responsive-sidebar-grid">
					<section className="responsive-main app-block-list">
						{components.propertySubmenu}
						{components.mortgage}
						{components.property}
						{components.finances}
						{components.valuations}
						{components.ads}
						{components.comps}
					</section>

					<aside className="responsive-secondary app-block-list">
						{components.progress}
						{components.pros}
						{components.blog}
					</aside>
				</section>
			</React.Fragment>
		);
	}
}

Dashboard.contextType = AuthContext;
export default withPropertyService(withBreakpoint(Dashboard));
